import axios from 'axios';

/**
 * @typedef {object} Gallery
 * @property {number}   id
 * @property {string}   title
 * @property {string}   description
 * @property {object}   author
 * @property {number}   author.id
 * @property {string}   author.name
 * @property {number[]} author.group
 * @property {string}   [medium]
 * @property {string}   [subjectMatter]
 * @property {string}   [term] - term id
 * @property {number}   [path] - path id
 * @property {number}   [assignment] - assignment id
 * @property {number[]} softwareUsed - software ids
 * @property {number[]} tags - tag ids
 * @property {object[]} likes
 * @property {number}   likes[].id - user id
 * @property {string}   likes[].name - user first+last name
 * @property {number}   viewCount
 * @property {object}   [featuredMedia]
 * @property {number}   featuredMedia.id
 * @property {string}   featuredMedia.type
 * @property {string}   featuredMedia.url
 * @property {string}   [featuredMedia.caption]
 * @property {object}   featuredMedia.crop
 * @property {number}   featuredMedia.crop.left
 * @property {number}   featuredMedia.crop.top
 * @property {number}   featuredMedia.crop.width
 * @property {number}   featuredMedia.crop.height
 * @property {object[]} secondaryMedia
 * @property {number}   secondaryMedia[].id
 * @property {string}   secondaryMedia[].type
 * @property {string}   secondaryMedia[].url
 * @property {string}   [secondaryMedia[].caption]
 * @property {boolean}  highlighted
 */

/**
 * @typedef {object} GalleryBrief
 * @property {number}   id
 * @property {string}   title
 * @property {object}   author
 * @property {number}   author.id
 * @property {string}   author.name
 * @property {object}   thumbnailMedia
 * @property {string}   thumbnailMedia.type
 * @property {string}   thumbnailMedia.url
 */

export default {

    /**
     * @async
     * @param {object}   gallery
     * @param {number}   gallery.createdBy
     * @param {string}   gallery.title
     * @param {string}   [gallery.description]
     * @param {string}   [gallery.medium]
     * @param {string}   [gallery.subjectMatter]
     * @param {string}   [gallery.term]
     * @param {number}   [gallery.path]
     * @param {number}   [gallery.assignment]
     * @param {number[]} [gallery.tags]         - tag ids
     * @param {number[]} [gallery.softwareUsed] - software ids
     * @param {object[]} gallery.secondaryMedia
     * @param {string}   gallery.secondaryMedia[].type
     * @param {string}   gallery.secondaryMedia[].url
     * @param {string}   gallery.secondaryMedia[].caption
     * @param {object}   [gallery.featuredMedia]
     * @param {string}   gallery.featuredMedia.type
     * @param {string}   gallery.featuredMedia.url
     * @param {object}   gallery.featuredMedia.crop
     * @param {number}   gallery.featuredMedia.crop.left
     * @param {number}   gallery.featuredMedia.crop.top
     * @param {number}   gallery.featuredMedia.crop.width
     * @param {number}   gallery.featuredMedia.crop.height
     * @param {boolean}  [gallery.highlighted]
     * @returns {object} gallery - see getGallery()
     */
    createGallery(gallery) {
        var options = {
            method: 'POST',
            baseURL: window.apiUrl,
            url: '/galleries',
            data: gallery,
            headers: {
                'Authorization': 'Bearer ' + localStorage.token
            }
        };
        return axios.request(options).then(response => {
            if (!response.data.success) {
                throw new Error(response.data.error);
            }
            return response.data.gallery;
        });
    },

    /**
     * @async
     * @param {object}   [params]
     * @param {number}   [params.limit=50]
     * @param {number}   [params.offset]
     * @param {string}   [params.order='latest'] - one of ['latest', 'trending', 'likes', 'comments']
     * @param {string}   [params.term] - term id
     * @param {number}   [params.path] - path id
     * @param {number[]} [params.assignments] - assignment ids
     * @param {number}   [params.author] - user id
     * @param {number}   [params.group] - group id
     * @param {number[]} [params.softwareUsed]
     * @param {number[]} [params.tags]
     * @param {string}   [params.highlighted]
     * @param {string}   [params.search]
     * @returns {object}         res
     * @returns {GalleryBrief[]} res.galleries
     * @returns {number}         res.totalGalleries
     */
    getGalleries(params = {}) {
        if (typeof params.limit !== 'number') {
            params.limit = 50;
        }
        if (typeof params.order !== 'string') {
            params.order = 'latest';
        }
        var options = {
            method: 'GET',
            baseURL: window.apiUrl,
            url: '/galleries',
            params: {
                limit:        params.limit,
                offset:       params.offset,
                order:        params.order,
                term:         params.term,
                path:         params.path,
                assignments:  params.assignments,
                author:       params.author,
                group:        params.group,
                softwareUsed: params.softwareUsed,
                tags:         params.tags,
                highlighted:  params.highlighted,
                search:       params.search,
                brief:        true,
                // cache:        'galleries'
            },
            headers: {
                'Authorization': 'Bearer ' + localStorage.token
            }
        };
        return axios.request(options).then(response => {
            if (!response.data.success) {
                throw new Error(response.data.error);
            }
            return {
                galleries: response.data.galleries,
                totalGalleries: response.data.totalGalleries
            };
        });
    },

    /**
     * @async
     * @param {number} galleryId
     * @returns {Gallery} gallery
     */
    getGallery(galleryId) {
        var options = {
            method: 'GET',
            baseURL: window.apiUrl,
            url: '/galleries/' + galleryId,
            headers: {
                'Authorization': 'Bearer ' + localStorage.token
            }
        };
        return axios.request(options).then(response => {
            if (!response.data.success) {
                throw new Error(response.data.error);
            }
            return response.data.gallery;
        });
    },

    /**
     * @async
     * @param {number}   galleryId
     * @param {object}   fields
     * @param {string}   [fields.title]
     * @param {string}   [fields.description]
     * @param {string}   [fields.medium]
     * @param {string}   [fields.subjectMatter]
     * @param {string}   [fields.term]
     * @param {number}   [fields.path]
     * @param {number}   [fields.assignment]
     * @param {number[]} [fields.tags]         - tag ids
     * @param {number[]} [fields.softwareUsed] - software ids
     * @param {object[]} [fields.secondaryMedia]
     * @param {string}   fields.secondaryMedia[].type
     * @param {string}   fields.secondaryMedia[].url
     * @param {string}   fields.secondaryMedia[].caption
     * @param {object}   [fields.featuredMedia]
     * @param {string}   fields.featuredMedia.type
     * @param {string}   fields.featuredMedia.url
     * @param {boolean}  [fields.highlighted]
     */
    updateGallery(galleryId, fields) {
        var options = {
            method: 'PATCH',
            baseURL: window.apiUrl,
            url: '/galleries/' + galleryId,
            data: fields,
            headers: {
                'Authorization': 'Bearer ' + localStorage.token
            }
        };
        return axios.request(options).then(response => {
            if (!response.data.success) {
                throw new Error(response.data.error);
            }
        });
    },

    /**
     * @async
     * @param {number} galleryId
     * @param {number} userId
     * @returns {object[]} likes
     */
    likeGallery(galleryId, userId) {
        var options = {
            method: 'PUT',
            baseURL: window.apiUrl,
            url: '/galleries/' + galleryId + '/likes/' + userId,
            headers: {
                'Authorization': 'Bearer ' + localStorage.token
            }
        };
        return axios.request(options).then(response => {
            if (!response.data.success) {
                throw new Error(response.data.error);
            }
            return response.data.likes;
        });
    },

    /**
     * @async
     * @param {number} galleryId
     * @param {number} userId
     * @returns {object[]} likes
     */
    unlikeGallery(galleryId, userId) {
        var options = {
            method: 'DELETE',
            baseURL: window.apiUrl,
            url: '/galleries/' + galleryId + '/likes/' + userId,
            headers: {
                'Authorization': 'Bearer ' + localStorage.token
            }
        };
        return axios.request(options).then(response => {
            if (!response.data.success) {
                throw new Error(response.data.error);
            }
            return response.data.likes;
        });
    },

    /**
     * @async
     * @param {number} galleryId
     */
    viewGallery(galleryId) {
        var options = {
            method: 'POST',
            baseURL: window.apiUrl,
            url: '/galleries/' + galleryId + '/views',
            headers: {
                'Authorization': 'Bearer ' + localStorage.token
            }
        };
        return axios.request(options).then(response => {
            if (!response.data.success) {
                const err = new Error(response.data.error);
                err.viewExists = !!response.data.viewExists;
                throw err;
            }
            return response.data.viewCount;
        });
    },

    /**
     * @async
     * @param {number} galleryId
     */
    deleteGallery(galleryId) {
        var options = {
            method: 'DELETE',
            baseURL: window.apiUrl,
            url: '/galleries/' + galleryId,
            headers: {
                'Authorization': 'Bearer ' + localStorage.token
            }
        };
        return axios.request(options).then(response => {
            if (!response.data.success) {
                throw new Error(response.data.error);
            }
        });
    },




    /**
     * @async
     * @param {number} galleryId
     * @param {object} comment
     * @param {string} comment.text
     * @param {number} comment.createdBy
     * @returns {object}   comment
     * @returns {number}   comment.id
     * @returns {string}   comment.text
     * @returns {number}   comment.createdBy
     * @returns {object}   comment.author
     * @returns {number}   comment.author.id
     * @returns {string}   comment.author.name
     * @returns {object[]} comment.likes
     * @returns {number}   comment.likes[].id
     * @returns {string}   comment.likes[].name
     */
    addGalleryComment(galleryId, comment) {
        var options = {
            method: 'POST',
            baseURL: window.apiUrl,
            url: '/galleries/' + galleryId + '/comments',
            data: comment,
            headers: {
                'Authorization': 'Bearer ' + localStorage.token
            }
        };
        return axios.request(options).then(response => {
            if (!response.data.success) {
                throw new Error(response.data.error);
            }
            return response.data.comment;
        });
    },

    /**
     * @async
     * @param {number} galleryId
     * @returns {object[]} comments
     * @returns {number}   comments[].id
     * @returns {object}   comments[].author
     * @returns {number}   comments[].author.id
     * @returns {string}   comments[].author.name
     * @returns {string}   comments[].text
     * @returns {object[]} comments[].likes - same structure as gallery likes
     * @returns {number}   comments[].likes[].id
     * @returns {string}   comments[].likes[].name
     */
    getGalleryComments(galleryId) {
        var options = {
            method: 'GET',
            baseURL: window.apiUrl,
            url: '/galleries/' + galleryId + '/comments',
            headers: {
                'Authorization': 'Bearer ' + localStorage.token
            }
        };
        return axios.request(options).then(response => {
            if (!response.data.success) {
                throw new Error(response.data.error);
            }
            return response.data.comments;
        });
    },

    /**
     * @async
     * @param {number} galleryId
     * @param {number} commentId
     * @param {number} userId
     * @returns {object[]} likes
     */
    likeGalleryComment(galleryId, commentId, userId) {
        var options = {
            method: 'PUT',
            baseURL: window.apiUrl,
            url: '/galleries/' + galleryId + '/comments/' + commentId + '/likes/' + userId,
            headers: {
                'Authorization': 'Bearer ' + localStorage.token
            }
        };
        return axios.request(options).then(response => {
            if (!response.data.success) {
                throw new Error(response.data.error);
            }
            return response.data.likes;
        });
    },

    /**
     * @async
     * @param {number} galleryId
     * @param {number} commentId
     * @param {number} userId
     * @returns {object[]} likes
     */
    unlikeGalleryComment(galleryId, commentId, userId) {
        var options = {
            method: 'DELETE',
            baseURL: window.apiUrl,
            url: '/galleries/' + galleryId + '/comments/' + commentId + '/likes/' + userId,
            headers: {
                'Authorization': 'Bearer ' + localStorage.token
            }
        };
        return axios.request(options).then(response => {
            if (!response.data.success) {
                throw new Error(response.data.error);
            }
            return response.data.likes;
        });
    },

    /**
     * @async
     * @param {number} galleryId
     * @param {number} commentId
     * @param {object} fields
     * @param {string} [fields.text]
     */
    updateGalleryComment(galleryId, commentId, fields) {
        var options = {
            method: 'PATCH',
            baseURL: window.apiUrl,
            url: '/galleries/' + galleryId + '/comments/' + commentId,
            data: fields,
            headers: {
                'Authorization': 'Bearer ' + localStorage.token
            }
        };
        return axios.request(options).then(response => {
            if (!response.data.success) {
                throw new Error(response.data.error);
            }
        });
    },

    /**
     * @async
     * @param {number} galleryId
     * @param {number} commentId
     */
    deleteGalleryComment(galleryId, commentId) {
        var options = {
            method: 'DELETE',
            baseURL: window.apiUrl,
            url: '/galleries/' + galleryId + '/comments/' + commentId,
            headers: {
                'Authorization': 'Bearer ' + localStorage.token
            }
        };
        return axios.request(options).then(response => {
            if (!response.data.success) {
                throw new Error(response.data.error);
            }
        });
    },




    /**
     * @async
     * @param {object} software
     * @param {string} software.name
     * @returns {object} software
     * @returns {number} software.id
     * @returns {string} software.name
     * @returns {string} [software.url]
     */
    createSoftware(software) {
        var options = {
            method: 'POST',
            baseURL: window.apiUrl,
            url: '/gallery-software',
            data: software,
            headers: {
                'Authorization': 'Bearer ' + localStorage.token
            }
        };
        return axios.request(options).then(response => {
            if (!response.data.success) {
                throw new Error(response.data.error);
            }
            return response.data.software;
        });
    },

    /**
     * @async
     * @param {number} softwareId
     * @returns {object} software
     * @returns {number} software.id
     * @returns {string} software.name
     * @returns {string} [software.url]
     */
    getSoftware(softwareId) {
        var options = {
            method: 'GET',
            baseURL: window.apiUrl,
            url: '/gallery-software/' + softwareId,            
            headers: {
                'Authorization': 'Bearer ' + localStorage.token
            }
        };
        return axios.request(options).then(response => {
            if (!response.data.success) {
                throw new Error(response.data.error);
            }
            return response.data.software;
        });
    },

    /**
     * @async
     * @returns {object[]} software
     * @returns {number}   software[].id
     * @returns {string}   software[].name
     * @returns {string}   [software[].url]
     */
    getAllSoftware() {
        var options = {
            method: 'GET',
            baseURL: window.apiUrl,
            url: '/gallery-software',
            params: {
                // cache: 'gallerysoftware'
            },
            headers: {
                'Authorization': 'Bearer ' + localStorage.token
            }
        };
        return axios.request(options).then(response => {
            if (!response.data.success && !response.data.software) {
                throw new Error(response.data.error);
            }
            return response.data.software;
        });
    },

    /**
     * @async
     * @param {string} searchTerm
     * @returns {object[]} software
     * @returns {number}   software[].id
     * @returns {string}   software[].name
     * @returns {string}   [software[].url]
     */
    searchSoftware(searchTerm) {
        var options = {
            method: 'GET',
            baseURL: window.apiUrl,
            url: '/gallery-software',
            params: {
                q: searchTerm
            },
            headers: {
                'Authorization': 'Bearer ' + localStorage.token
            }
        };
        return axios.request(options).then(response => {
            if (!response.data.success) {
                throw new Error(response.data.error);
            }
            return response.data.software;
        });
    },




    /**
     * @async
     * @param {object} tag
     * @param {string} tag.name
     * @returns {object} tag
     * @returns {number} tag.id
     * @returns {string} tag.name
     */
    createTag(tag) {
        var options = {
            method: 'POST',
            baseURL: window.apiUrl,
            url: '/gallery-tags',
            data: tag,
            params: {
                ensureExists: true
            },
            headers: {
                'Authorization': 'Bearer ' + localStorage.token
            }
        };
        return axios.request(options).then(response => {
            if (!response.data.success) {
                console.log(response);
                throw new Error(response.data.error);
            }
            return response.data.tag;
        });
    },

    /**
     * @async
     * @param {number} tagId
     * @returns {object} tag
     * @returns {number} tag.id
     * @returns {string} tag.name
     */
    getTag(tagId) {
        var options = {
            method: 'GET',
            baseURL: window.apiUrl,
            url: '/gallery-tags/' + tagId,
            headers: {
                'Authorization': 'Bearer ' + localStorage.token
            }
        };
        return axios.request(options).then(response => {
            if (!response.data.success) {
                throw new Error(response.data.error);
            }
            return response.data.tag;
        });
    },

    /**
     * @async
     * @param {string} searchTerm
     * @returns {object[]} tags
     * @returns {number}   tags[].id
     * @returns {string}   tags[].name
     */
    searchTags(searchTerm) {
        var options = {
            method: 'GET',
            baseURL: window.apiUrl,
            url: '/gallery-tags',
            params: {
                q: searchTerm,
                // cache: 'gallerytags',
            },
            headers: {
                'Authorization': 'Bearer ' + localStorage.token
            }
        };
        return axios.request(options).then(response => {
            if (!response.data.success) {
                throw new Error(response.data.error);
            }
            return response.data.tags;
        });
    },




    /**
     * @async
     * @returns {object}   filters
     * @returns {object[]} filters.terms
     * @returns {string}   filters.terms[].id
     * @returns {string}   filters.terms[].name
     * @returns {string}   filters.terms[].type
     * @returns {object[]} filters.paths
     * @returns {number}   filters.paths[].id
     * @returns {string}   filters.paths[].name
     * @returns {object[]} filters.groups
     * @returns {number}   filters.groups[].id
     * @returns {string}   filters.groups[].label
     * @returns {string}   filters.groups[].term
     * @returns {object[]} filters.assignments
     * @returns {number}   filters.assignments[].id
     * @returns {string}   filters.assignments[].name
     * @returns {string[]} filters.assignments[].terms
     * @returns {number[]} filters.assignments[].paths
     * @returns {number}   filters.assignments[].galleryCount
     */
    getFilters() {
        var options = {
            method: 'GET',
            baseURL: window.apiUrl,
            url: '/gallery-filters',
            headers: {
                'Authorization': 'Bearer ' + localStorage.token
            }
        };
        return axios.request(options).then(response => {
            if (!response.data.success) {
                throw new Error(response.data.error);
            }
            const filters = response.data.filters;
            if (filters.paths) {
                // filters.paths = filters.paths.filter(p => p.id != 9200)
            }
            return filters;
        });
    },

    getCloudinaryField(field) {
        var options = {
            method: 'GET',
            baseURL: window.apiUrl,
            url: '/cloudinary-field?field='+field,
            headers: {
                'Authorization': 'Bearer ' + localStorage.token
            }
        };
        return axios.request(options).then(response => {
            if (!response.data.success) {
                throw new Error(response.data.error);
            }
            return response.data;
        });
    }
};



// WEBPACK FOOTER //
// ./src/services/galleries/index.js